import { createContext, useContext, useEffect, useRef, useState, useCallback, type ReactNode } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  getAccessToken,
  refreshAccessToken,
  clearTokens,
  setSessionExpiredHandler,
  logout as apiLogout,
} from '../lib/auth-api';

interface AuthUser {
  id: string;
  email: string;
}

interface AuthState {
  /** Whether the user is authenticated (has a valid access token) */
  isAuthenticated: boolean;
  /** Current user info (decoded from JWT) */
  user: AuthUser | null;
  /** True while checking initial auth state */
  loading: boolean;
  /** Sign out the current user */
  signOut: () => Promise<void>;
  /** Update auth state after login/register (call after setAccessToken) */
  setAuthenticated: (user: AuthUser) => void;
  // Legacy compat: session is non-null when authenticated
  session: { access_token: string } | null;
}

const AuthContext = createContext<AuthState | undefined>(undefined);

/**
 * Decode a JWT payload without verification (client-side convenience).
 */
function decodeJwtPayload(token: string): Record<string, unknown> | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    return JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
  } catch {
    return null;
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [expiryToast, setExpiryToast] = useState(false);
  const manualSignOut = useRef(false);
  const navigate = useNavigate();
  const location = useLocation();

  // Public routes that should not redirect to /login on session expiry
  const PUBLIC_PATHS = ['/', '/login', '/signup', '/reset-password'];
  const isPublicPath = (path: string) =>
    PUBLIC_PATHS.includes(path) || path.startsWith('/auth/');

  // Handle session expiry (called by auth-api when refresh fails for an established session)
  useEffect(() => {
    setSessionExpiredHandler(() => {
      if (!manualSignOut.current) {
        setUser(null);
        // Only show toast + redirect if user was on a protected page
        if (!isPublicPath(location.pathname)) {
          setExpiryToast(true);
          setTimeout(() => {
            setExpiryToast(false);
            navigate('/login');
          }, 3000);
        }
      }
      manualSignOut.current = false;
    });
  }, [navigate, location.pathname]);

  // On mount, try to restore the session via refresh token
  useEffect(() => {
    (async () => {
      const token = getAccessToken();
      if (token) {
        // Already have a token in memory (e.g., just logged in)
        const payload = decodeJwtPayload(token);
        if (payload) {
          setUser({ id: payload.sub as string, email: payload.email as string });
        }
        setLoading(false);
        return;
      }

      // Try to refresh from httpOnly cookie — silent so no expiry toast on first visit
      const refreshed = await refreshAccessToken(true);
      if (refreshed) {
        const newToken = getAccessToken();
        if (newToken) {
          const payload = decodeJwtPayload(newToken);
          if (payload) {
            setUser({ id: payload.sub as string, email: payload.email as string });
          }
        }
      }
      setLoading(false);
    })();
  }, []);

  const signOut = useCallback(async () => {
    manualSignOut.current = true;
    try {
      await apiLogout();
    } catch {
      // Even if API call fails, clear local state
      clearTokens();
    }
    setUser(null);
    navigate('/login');
  }, [navigate]);

  const setAuthenticated = useCallback((u: AuthUser) => {
    setUser(u);
  }, []);

  const isAuthenticated = !!user;
  const accessToken = getAccessToken();

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        user,
        loading,
        signOut,
        setAuthenticated,
        session: isAuthenticated && accessToken ? { access_token: accessToken } : null,
      }}
    >
      {children}
      {expiryToast && (
        <div className="fixed bottom-4 right-4 bg-yellow-50 border border-yellow-200 text-yellow-800 px-4 py-3 rounded-lg text-sm shadow-lg z-[2000]">
          Votre session a expiré, veuillez vous reconnecter.
        </div>
      )}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
